<template>
	<div class="purchases-journal purchases-typography">
		<div v-if="storeError" class="inline-error">
			<p>{{ storeError }}</p>
			<button class="outline-btn" type="button" @click="refresh">Retry</button>
		</div>
		<div class="purchase-list-container">
		<div class="purchase-header">
			<button class="action-btn" type="button" @click="handleAdd" :disabled="isLoading || isAdding">
				<JournalIcon name="plus" />
				<span>Add purchase</span>
			</button>
			<section class="filters-bar" :class="{ 'is-disabled': filtersDisabled }" aria-label="Purchase filters">
				<div class="filters-bar-start">
					<JournalIcon name="filter" />
				</div>
				<FilterMotion :expanded="editingFilterType === 'vendor' && !localFilters.vendorId">
					<div v-if="localFilters.vendorId" class="filter-chip">
						<span class="filter-chip-text">{{ resolvedVendorName }}</span>
						<button class="filter-chip-remove" type="button" aria-label="Remove vendor filter" @click.stop="removeFilter('vendorId')">
							<JournalIcon name="close" :size="14" />
						</button>
					</div>
					<div v-else-if="editingFilterType === 'vendor'" class="filter-inline-editor" @click.stop>
						<CatalogSearchInput use-fixed
							ref="vendorFilterRef"
							:modelValue="localFilters.vendorId"
							@update:modelValue="(val) => handleFilterValue('vendorId', val)"
							:options="vendorOptions"
							placeholder="Search vendor..."
							class="filter-editor-search"
						/>
					</div>
					<button v-else class="filter-label-btn" type="button" :disabled="filtersDisabled" @click.stop="startFilterEdit('vendor')">
						Vendor
					</button>
				</FilterMotion>
				<FilterMotion :expanded="editingFilterType === 'building' && !localFilters.buildingId">
					<div v-if="localFilters.buildingId" class="filter-chip">
						<span class="filter-chip-text">{{ resolvedBuildingName }}</span>
						<button class="filter-chip-remove" type="button" aria-label="Remove building filter" @click.stop="removeFilter('buildingId')">
							<JournalIcon name="close" :size="14" />
						</button>
					</div>
					<div v-else-if="editingFilterType === 'building'" class="filter-inline-editor" @click.stop>
						<CatalogSearchInput use-fixed
							ref="buildingFilterRef"
							:modelValue="localFilters.buildingId"
							@update:modelValue="(val) => handleFilterValue('buildingId', val)"
							:options="buildingOptions"
							placeholder="Search building..."
							class="filter-editor-search"
						/>
					</div>
					<button v-else class="filter-label-btn" type="button" :disabled="filtersDisabled" @click.stop="startFilterEdit('building')">
						Building
					</button>
				</FilterMotion>
				<div class="filter-date-wrapper" :class="{ 'has-value': localFilters.dateFrom || localFilters.dateTo }">
					<DateRangePicker
						:dateFrom="localFilters.dateFrom"
						:dateTo="localFilters.dateTo"
						:useFixed="true"
						animated
						:disabled="filtersDisabled"
						placeholder="Date range"
						:show-trigger-icon="false"
						@update="handleDateRangeUpdate"
					>
						<template #previous-icon><JournalIcon name="chevron-left" /></template>
						<template #next-icon><JournalIcon name="chevron-right" /></template>
					</DateRangePicker>
					<button v-if="localFilters.dateFrom || localFilters.dateTo" class="filter-chip-remove" type="button" aria-label="Clear date range" :disabled="filtersDisabled" @click.stop="removeFilter('dateRange')">
						<JournalIcon name="close" :size="14" />
					</button>
				</div>
			</section>
		</div>

			<div class="table-wrapper" v-if="showInitialLoading">
				<div class="skeleton-block"></div>
			</div>

		<div v-else class="purchase-list-content">
			<div class="purchase-row purchase-header-row">
				<div class="purchase-fields header-fields">
					<div class="field-header field-status">Status</div>
					<div class="field-header field-date">Date</div>
					<div class="field-header field-vendor">Vendor</div>
					<div class="field-header field-cos">COS</div>
					<div class="field-header field-building">Building</div>
					<div class="field-header field-gross">Gross</div>
					<div class="field-header field-net">Net</div>
					<div class="field-header field-vat">VAT</div>
					<div class="field-header field-notes">Notes</div>
				</div>
				<div class="purchase-actions header-actions" aria-hidden="true">
					<div class="action-wrapper">
						<span class="more-options-btn" style="visibility: hidden;">
							<JournalIcon name="more" class="icon-more" />
						</span>
					</div>
				</div>
			</div>
			<div v-if="items.length" class="purchase-list">
					<template v-for="purchase in items" :key="rowKey(purchase)">
						<div v-if="editingValues[purchase.id]" v-row-enter="newRowKeys.has(rowKey(purchase))" class="purchase-row" :class="{
							'is-pending': purchase.__pending,
							'submenu-open': openSubmenuId === purchase.id,
						}">
							<div class="purchase-fields">
								<div class="field-input-container field-status">
									<PurchaseStatusSelect
										v-model="editingValues[purchase.id].documentStatus"
										:options="documentStatusOptions"
										:disabled="!!purchase.__pending"
										aria-label="Purchase status"
										@change="handleStatusChange(purchase)"
									/>
								</div>
								<div class="field-input-container field-date">
									<DateRangePicker
										:ref="(input) => setCellInput(purchase.id, 'date', input)"
										mode="single"
										use-fixed
										animated
										:dateFrom="editingValues[purchase.id].date"
										:dateTo="editingValues[purchase.id].date"
										:disabled="!!purchase.__pending"
										trigger-label="Purchase date"
										placeholder="Select date"
										@update="({ dateFrom }) => updateSelectValue(purchase, 'date', dateFrom)"
									>
										<template #trigger-icon><JournalIcon name="calendar" :size="12" /></template>
										<template #previous-icon><JournalIcon name="chevron-left" /></template>
										<template #next-icon><JournalIcon name="chevron-right" /></template>
									</DateRangePicker>
								</div>
								<div class="field-input-container field-vendor">
									<CatalogSearchInput use-fixed :modelValue="editingValues[purchase.id].vendorId"
										:ref="(input) => setCellInput(purchase.id, 'vendorId', input)"
										@update:modelValue="(val) => updateSelectValue(purchase, 'vendorId', val)"
										:options="vendorOptions" :fallback-label="purchase.vendor?.name || ''" placeholder="Vendor" v-tooltip />
								</div>
								<div class="field-input-container field-cos">
									<CatalogSearchInput use-fixed :modelValue="editingValues[purchase.id].cosId"
										:ref="(input) => setCellInput(purchase.id, 'cosId', input)"
										@update:modelValue="(val) => updateSelectValue(purchase, 'cosId', val)"
										:options="cosOptions" :fallback-label="purchase.cos?.name || ''" placeholder="COS" v-tooltip />
								</div>
								<div class="field-input-container field-building">
									<CatalogSearchInput use-fixed :modelValue="editingValues[purchase.id].buildingId"
										:ref="(input) => setCellInput(purchase.id, 'buildingId', input)"
										@update:modelValue="(val) => updateSelectValue(purchase, 'buildingId', val)"
										:options="buildingOptions" :fallback-label="purchase.building?.name || ''" placeholder="Building" v-tooltip />
								</div>
								<div class="field-input-container field-number field-gross">
									<input type="number" inputmode="decimal" step="0.01" min="0" v-model="editingValues[purchase.id].gross"
										:ref="(input) => setCellInput(purchase.id, 'gross', input)"
										@focus="handleMoneyFocus(purchase, 'gross')"
										@keydown.enter.prevent="(event) => handleMoneyEnter(purchase, 'gross', event)"
										@input="handleMoneyInput(purchase, 'gross')"
										@blur="handleMoneyBlur(purchase, 'gross')" class="field-input field-number"
										placeholder="Gross" />
								</div>
								<div class="field-input-container field-number field-net">
									<input type="number" inputmode="decimal" step="0.01" min="0" v-model="editingValues[purchase.id].net"
										:ref="(input) => setCellInput(purchase.id, 'net', input)"
										@focus="handleMoneyFocus(purchase, 'net')"
										@keydown.enter.prevent="(event) => handleMoneyEnter(purchase, 'net', event)"
										@input="handleMoneyInput(purchase, 'net')"
										@blur="handleMoneyBlur(purchase, 'net')" class="field-input field-number"
										placeholder="Net" />
								</div>
								<div class="field-input-container field-number field-vat">
									<input type="number" inputmode="decimal" step="0.01" min="0" v-model="editingValues[purchase.id].vat"
										:ref="(input) => setCellInput(purchase.id, 'vat', input)"
										@focus="handleMoneyFocus(purchase, 'vat')"
										@keydown.enter.prevent="(event) => handleMoneyEnter(purchase, 'vat', event)"
										@input="handleMoneyInput(purchase, 'vat')"
										@blur="handleMoneyBlur(purchase, 'vat')" class="field-input field-number"
										placeholder="VAT" />
									<button type="button" class="vat-toggle-btn" :aria-label="`Change VAT rate, currently ${getVatRateFor(purchase.id)}%`" @click.stop="toggleVatRate(purchase)">
										{{ getVatRateFor(purchase.id) }}%
									</button>
								</div>
								<div class="field-input-container field-notes">
									<input type="text" v-model="editingValues[purchase.id].notes"
										:ref="(input) => setCellInput(purchase.id, 'notes', input)"
										@input="requestSave(purchase)" class="field-input" placeholder="Notes" />
								</div>
							</div>
							<div class="purchase-actions">
								<div class="action-wrapper">
									<button @click.stop="toggleSubmenu(purchase.id)" title="More options" aria-label="More options"
										class="more-options-btn">
										<JournalIcon name="more" class="icon-more" />
									</button>
									<div v-if="openSubmenuId === purchase.id" class="submenu" v-floating-menu>
										<HoldToConfirm
											class="submenu-btn delete"
											:disabled="isRowSaving(purchase.id)"
											@confirm="deletePurchase(purchase)"
											@cancel="openSubmenuId = null"
										>
											Hold to delete purchase
										</HoldToConfirm>
									</div>
								</div>
							</div>
						</div>
					</template>
				</div>
				<div v-if="!items.length" class="empty-state">
					<p>No purchases match the current filters.</p>
				</div>
				<div
					v-if="showTableLoadingOverlay"
					class="table-loading-overlay"
					role="status"
					aria-live="polite"
				>
					<div class="table-loading-indicator">
						<span class="table-loading-spinner" aria-hidden="true"></span>
						<span>Loading purchases…</span>
					</div>
				</div>
			</div>

			<div v-if="items.length && isDateRangeSelected" class="purchase-totals-footer">
				<div class="totals-footer-count">Results: {{ resultsStart }} - {{ resultsEnd }} of {{ pagination.total }}</div>
				<div class="totals-footer-summary">
					<div class="totals-line">
						<span class="totals-label">Subtotal</span>
						<span class="totals-value">£{{ formattedGrossSummary.subtotal }}</span>
					</div>
					<div class="totals-line">
						<span class="totals-label">Refunded</span>
						<span class="totals-value">-£{{ formattedGrossSummary.refunded }}</span>
					</div>
					<div class="totals-line total">
						<span class="totals-label">Total</span>
						<span class="totals-value">£{{ formattedGrossSummary.total }}</span>
					</div>
				</div>
			</div>

			<footer class="pagination-bar" v-if="!isDateRangeSelected && (items.length || pagination.total)">
				<div class="pagination-controls" v-if="!isDateRangeSelected">
					<button class="pagination-btn prev" aria-label="Previous page" @click="changePage(pagination.page - 1)"
						:disabled="pagination.page === 1">
						<JournalIcon name="chevron-left" />
					</button>
					<template v-for="(p, index) in visiblePages" :key="index">
						<span v-if="p === '...'" class="pagination-ellipsis">...</span>
						<button v-else class="pagination-btn page-number" :class="{ active: p === pagination.page }" :aria-current="p === pagination.page ? 'page' : undefined"
							@click="changePage(p)">
							{{ p }}
						</button>
					</template>
					<button class="pagination-btn next" aria-label="Next page" @click="changePage(pagination.page + 1)"
						:disabled="pagination.page >= pagination.pageCount">
						<JournalIcon name="chevron-right" />
					</button>
				</div>
				<div class="pagination-results">
					Results: {{ resultsStart }} - {{ resultsEnd }} of {{ pagination.total }}
				</div>
			</footer>
		</div>
	</div>
</template>

<script setup>
import vFloatingMenu from '@/directives/floatingMenu';
import vRowEnter from '@/directives/rowEnter';
import { sameRecords, useLiveRefresh } from '@/composables/useLiveRefresh';
import { computed, getCurrentInstance, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, reactive, ref, watch } from 'vue';
import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import { useJournalPurchasesStore } from '@/stores/journalPurchases';
import { toBig } from '@/composables/useInvoiceCalculations';
import JournalIcon from '@/components/icons/AppointmentIcon.vue';
import FilterMotion from '@/components/journal/FilterMotion.vue';
import PurchaseStatusSelect from '@/components/journal/PurchaseStatusSelect.vue';
import HoldToConfirm from '@/components/HoldToConfirm.vue';
import { PURCHASE_DOCUMENT_STATUSES } from '@/constants/purchaseStatuses';
import { PURCHASE_VAT_RATES as VAT_RATE_SEQUENCE, DEFAULT_PURCHASE_VAT_RATE as DEFAULT_VAT_RATE, inferPurchaseVatRate, recalculatePurchaseAmounts } from '@/utils/purchaseAmounts';

const DOCUMENT_STATUS_OPTIONS = PURCHASE_DOCUMENT_STATUSES;

const store = useJournalPurchasesStore();
const localFilters = reactive({
	vendorId: '',
	buildingId: '',
	dateFrom: '',
	dateTo: '',
});
const editingValues = ref({});
const vatRates = reactive({});
const moneyFieldLocks = reactive({});
const vatRecalcGuards = reactive({});
const savingState = reactive({});
const saveTimers = reactive({});
const queuedSaves = reactive({});
const isAdding = ref(false);
const openSubmenuId = ref(null);
const editingFilterType = ref(null);
const vendorFilterRef = ref(null);
const buildingFilterRef = ref(null);
const pendingOverflowSnapshots = {};
let filterTimer = null;
const filtersInitialized = ref(false);
const instance = getCurrentInstance();
const logError = instance?.proxy?.$logError;
const toast = instance?.proxy?.$toast;

const items = computed(() => store.items);
const pagination = computed(() => store.pagination);
const vendorOptions = computed(() => store.vendorOptions);
const cosOptions = computed(() => store.cosOptions);
const buildingOptions = computed(() => store.buildingOptions);
const isLoading = computed(() => store.isLoading);
const storeError = computed(() => store.error);
const showInitialLoading = computed(() => isLoading.value && !items.value.length && !store.initialized);
const showTableLoadingOverlay = computed(
	() => isLoading.value && !showInitialLoading.value && !storeError.value,
);
const filtersDisabled = computed(() => isLoading.value);
const documentStatusOptions = DOCUMENT_STATUS_OPTIONS;

const resolvedVendorName = computed(() => {
	if (!localFilters.vendorId) return '';
	const opt = vendorOptions.value.find((o) => String(o.id) === String(localFilters.vendorId));
	return opt?.name || opt?.label || String(localFilters.vendorId);
});

const resolvedBuildingName = computed(() => {
	if (!localFilters.buildingId) return '';
	const opt = buildingOptions.value.find((o) => String(o.id) === String(localFilters.buildingId));
	return opt?.name || opt?.label || String(localFilters.buildingId);
});

const filterRefMap = { vendor: vendorFilterRef, building: buildingFilterRef };

const startFilterEdit = (key) => {
	editingFilterType.value = key;
	nextTick(() => {
		const target = filterRefMap[key]?.value;
		if (!target) return;
		if (typeof target.focusInput === 'function') {
			target.focusInput();
		} else if (target.$el?.querySelector?.('input')) {
			target.$el.querySelector('input').focus();
		}
	});
};

const handleFilterValue = (field, value) => {
	localFilters[field] = value || '';
	if (value) {
		editingFilterType.value = null;
	}
};
const isDateRangeSelected = computed(() => !!localFilters.dateFrom || !!localFilters.dateTo);
const formatNumber = (value) => {
	const numeric = Number(toBig(value).toFixed(2));
	const safe = numeric === 0 ? 0 : numeric;
	return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(safe);
};

const totals = computed(() => {
	let net = toBig(0);
	let vat = toBig(0);
	let gross = toBig(0);
	store.items.forEach((item) => {
		net = net.plus(toBig(item.net));
		vat = vat.plus(toBig(item.vat));
		gross = gross.plus(toBig(item.gross));
	});
	return { net, vat, gross };
});

const formattedTotals = computed(() => ({
	net: formatNumber(totals.value.net),
	vat: formatNumber(totals.value.vat),
	gross: formatNumber(totals.value.gross),
}));

const grossSummary = computed(() => {
	let subtotal = toBig(0);
	let refunded = toBig(0);
	store.items.forEach((item) => {
		const gross = toBig(item.gross);
		const status = String(item.documentStatus || '').toLowerCase();
		if (status === 'refund') {
			refunded = refunded.plus(gross.abs());
			return;
		}
		subtotal = subtotal.plus(gross);
	});
	return {
		subtotal,
		refunded,
		total: subtotal.minus(refunded),
	};
});

const formattedGrossSummary = computed(() => ({
	subtotal: formatNumber(grossSummary.value.subtotal),
	refunded: formatNumber(grossSummary.value.refunded),
	total: formatNumber(grossSummary.value.total),
}));

const resultsStart = computed(() => {
	if (pagination.value.total === 0) return 0;
	return (pagination.value.page - 1) * pagination.value.pageSize + 1;
});

const resultsEnd = computed(() => {
	return Math.min(pagination.value.page * pagination.value.pageSize, pagination.value.total);
});

const visiblePages = computed(() => {
	const count = pagination.value.pageCount || 1;
	const current = pagination.value.page;
	if (count <= 7) {
		return Array.from({ length: count }, (_, i) => i + 1);
	}
	const pages = [1];
	
	let start = Math.max(2, current - 1);
	let end = Math.min(count - 1, current + 1);
	
	if (current <= 4) {
		end = 5;
		if (current === 4) end = 6;
	}
	if (current >= count - 3) {
		start = count - 4;
		if (current === count - 3) start = count - 5;
	}
	
	if (start > 2) {
		pages.push('...');
	}
	
	for (let i = start; i <= end; i++) {
		pages.push(i);
	}
	
	if (end < count - 1) {
		pages.push('...');
	}
	if (count > 1) {
		pages.push(count);
	}
	return pages;
});

const vatRateKey = (id) => {
	if (id === null || id === undefined) return null;
	return String(id);
};

const clampVatRate = (rate) => {
	const numeric = Number(rate);
	return VAT_RATE_SEQUENCE.includes(numeric) ? numeric : DEFAULT_VAT_RATE;
};

const getVatRateFor = (id) => {
	const key = vatRateKey(id);
	if (!key) return DEFAULT_VAT_RATE;
	const stored = vatRates[key];
	return typeof stored === 'number' ? stored : DEFAULT_VAT_RATE;
};

const getStoredVatRate = (id) => {
	const key = vatRateKey(id);
	if (!key) return undefined;
	return vatRates[key];
};

const setVatRateFor = (id, rate) => {
	const key = vatRateKey(id);
	if (!key) return;
	vatRates[key] = clampVatRate(rate);
};

const deleteVatRateFor = (id) => {
	const key = vatRateKey(id);
	if (!key) return;
	delete vatRates[key];
};

const parseMoneyValue = (value) => {
	if (value === null || value === undefined) return null;
	const text = String(value).trim();
	if (!text) return null;
	return toBig(text);
};

const isEmptyMoneyInput = (value) => {
	if (value === null || value === undefined) return true;
	return String(value).trim() === '';
};

const formatMoneyValue = (value) => {
	if (value === null || value === undefined || value === '') return '';
	try {
		const fixed = toBig(value).toFixed(2);
		return fixed === '-0.00' ? '0.00' : fixed;
	} catch (error) {
		return '';
	}
};

const ensureVatRateEntry = (item) => {
	if (!item?.id) return;
	const key = vatRateKey(item.id);
	if (!key || vatRates[key] !== undefined) {
		return;
	}
	setVatRateFor(item.id, inferPurchaseVatRate(item));
};

const pruneVatRates = (validIds = []) => {
	const allowed = new Set((validIds || []).map((id) => String(id)));
	Object.keys(vatRates).forEach((key) => {
		if (!allowed.has(key)) {
			delete vatRates[key];
		}
	});
};

const getMoneyLockKey = (id) => vatRateKey(id);

const setMoneyFieldLock = (id, field, locked) => {
	const key = getMoneyLockKey(id);
	if (!key || !field) return;
	const next = { ...(moneyFieldLocks[key] || {}) };
	if (locked) {
		next[field] = true;
		moneyFieldLocks[key] = next;
	} else {
		delete next[field];
		if (Object.keys(next).length === 0) {
			delete moneyFieldLocks[key];
		} else {
			moneyFieldLocks[key] = next;
		}
	}
};

const clearMoneyLocksFor = (id) => {
	const key = getMoneyLockKey(id);
	if (!key) return;
	delete moneyFieldLocks[key];
};

const transferMoneyLocks = (fromId, toId) => {
	const fromKey = getMoneyLockKey(fromId);
	const toKey = getMoneyLockKey(toId);
	if (!fromKey || !toKey || !moneyFieldLocks[fromKey]) return;
	moneyFieldLocks[toKey] = {
		...(moneyFieldLocks[toKey] || {}),
		...moneyFieldLocks[fromKey],
	};
	delete moneyFieldLocks[fromKey];
};

const pruneMoneyLocks = (validIds = []) => {
	const allowed = new Set((validIds || []).map((id) => String(id)));
	Object.keys(moneyFieldLocks).forEach((key) => {
		if (!allowed.has(key)) {
			delete moneyFieldLocks[key];
		}
	});
};

const setVatRecalcGuard = (id, active) => {
	const key = getMoneyLockKey(id);
	if (!key) return;
	if (active) {
		vatRecalcGuards[key] = true;
	} else {
		delete vatRecalcGuards[key];
	}
};

const releaseVatRecalcGuard = (id) => {
	const key = getMoneyLockKey(id);
	if (!key || !vatRecalcGuards[key]) return;
	if (savingState[id] || queuedSaves[id]) return;
	delete vatRecalcGuards[key];
};

const pruneVatRecalcGuards = (validIds = []) => {
	const allowed = new Set((validIds || []).map((id) => String(id)));
	Object.keys(vatRecalcGuards).forEach((key) => {
		if (!allowed.has(key)) {
			delete vatRecalcGuards[key];
		}
	});
};

const snapshotEditingValues = (purchaseId) => {
	const current = editingValues.value?.[purchaseId];
	if (!current) return null;
	return { values: { ...current } };
};

const mergeEditedFields = (purchaseId, entry, snapshot) => {
	if (!snapshot?.values) return entry;
	const current = editingValues.value?.[purchaseId];
	if (!current) return entry;
	const merged = { ...entry };
	Object.keys(entry).forEach((field) => {
		if (current[field] !== snapshot.values[field] && current[field] !== undefined) {
			merged[field] = current[field];
		}
	});
	return merged;
};

const applyLockedMoneyFields = (id, entry) => {
	const key = getMoneyLockKey(id);
	if (!key) return entry;
	const locks = moneyFieldLocks[key];
	const hasVatRecalcGuard = !!vatRecalcGuards[key];
	if (!locks && !hasVatRecalcGuard) return entry;
	const current = editingValues.value?.[id];
	if (!current) return entry;
	const result = { ...entry };
	['gross', 'net', 'vat'].forEach((field) => {
		if (locks?.[field] || hasVatRecalcGuard) {
			result[field] = current[field] ?? result[field];
		}
	});
	return result;
};

const applyVatAutoCalc = (purchaseId, changedField) => {
	if (!purchaseId || !changedField) return;
	recalculatePurchaseAmounts(editingValues.value[purchaseId], changedField, getVatRateFor(purchaseId));
};

const handleMoneyInput = (purchase, field) => {
	if (!purchase?.id || !field) return;
	setMoneyFieldLock(purchase.id, field, true);
	const record = editingValues.value[purchase.id];
	if (record && isEmptyMoneyInput(record[field])) {
		record.gross = '';
		record.net = '';
		record.vat = '';
	} else {
		applyVatAutoCalc(purchase.id, field);
	}
	requestSave(purchase);
};

const formatMoneyField = (purchase, field, { debounce = false } = {}) => {
	if (!purchase?.id || !field) return;
	const record = editingValues.value[purchase.id];
	if (!record) return;
	record[field] = formatMoneyValue(record[field]);
	requestSave(purchase, { debounce });
};

const handleMoneyFocus = (purchase, field) => {
	if (!purchase?.id || !field) return;
	setMoneyFieldLock(purchase.id, field, true);
};

const handleMoneyBlur = (purchase, field) => {
	if (!purchase?.id || !field) return;
	setMoneyFieldLock(purchase.id, field, false);
	formatMoneyField(purchase, field, { debounce: false });
};

const toggleVatRate = (purchase) => {
	if (!purchase?.id) return;
	// Keep recalculated values stable while save is in-flight.
	setVatRecalcGuard(purchase.id, true);
	const current = getVatRateFor(purchase.id);
	const currentIndex = VAT_RATE_SEQUENCE.indexOf(current);
	const nextRate = VAT_RATE_SEQUENCE[(currentIndex + 1) % VAT_RATE_SEQUENCE.length];
	setVatRateFor(purchase.id, nextRate);
	const record = editingValues.value[purchase.id];
	if (!record) {
		releaseVatRecalcGuard(purchase.id);
		return;
	}
	const grossValue = parseMoneyValue(record.gross);
	const netValue = parseMoneyValue(record.net);
	const vatValue = parseMoneyValue(record.vat);

	if (grossValue !== null) {
		applyVatAutoCalc(purchase.id, 'gross');
	} else if (netValue !== null) {
		applyVatAutoCalc(purchase.id, 'net');
	} else if (vatValue !== null) {
		applyVatAutoCalc(purchase.id, 'vat');
	}
	requestSave(purchase, { debounce: false });
};

const syncFiltersFromStore = () => {
	const src = store.filters;
	localFilters.vendorId = src.vendorId || '';
	localFilters.buildingId = src.buildingId || '';
	localFilters.dateFrom = src.dateFrom || '';
	localFilters.dateTo = src.dateTo || '';
	filtersInitialized.value = true;
};

const debouncedUpdateFilters = () => {
	if (filterTimer) {
		clearTimeout(filterTimer);
		filterTimer = null;
	}
	filterTimer = setTimeout(async () => {
		try {
			await store.updateFilters({
				vendorId: localFilters.vendorId || null,
				buildingId: localFilters.buildingId || null,
				dateFrom: localFilters.dateFrom || null,
				dateTo: localFilters.dateTo || null,
			});
			seedEditingValues();
		} catch (error) {
			logError(error, 'PurchasesJournalView updateFilters');
		}
	}, 350);
};

watch(
	() => ({
		vendorId: localFilters.vendorId,
		buildingId: localFilters.buildingId,
		dateFrom: localFilters.dateFrom,
		dateTo: localFilters.dateTo,
	}),
	() => {
		if (!filtersInitialized.value) return;
		debouncedUpdateFilters();
	},
	{ deep: true }
);

watch(
	items,
	(newItems) => {
		ensureEditingEntries(newItems || []);
	},
	{ deep: false, immediate: true }
);

function toMoneyString(value) {
	return formatMoneyValue(value);
}

function buildEditingEntry(item) {
	return {
		date: item.date ? item.date.substring(0, 10) : '',
		vendorId: item.vendor?.id || '',
		cosId: item.cos?.id || '',
		buildingId: item.building?.id || '',
		notes: item.notes || '',
		documentStatus: item.documentStatus || '',
		net: toMoneyString(item.net),
		vat: toMoneyString(item.vat),
		gross: toMoneyString(item.gross),
	};
}

const seedEditingValues = () => {
	const map = {};
	store.items.forEach((item) => {
		if (!item?.id) return;
		ensureVatRateEntry(item);
		map[item.id] = applyLockedMoneyFields(item.id, buildEditingEntry(item));
	});
	editingValues.value = map;
	const validIds = Object.keys(map);
	pruneVatRates(validIds);
	pruneMoneyLocks(validIds);
	pruneVatRecalcGuards(validIds);
};

function ensureEditingEntries(entries = []) {
	const next = { ...editingValues.value };
	let changed = false;
	entries.forEach((item) => {
		if (!item?.id || next[item.id]) return;
		ensureVatRateEntry(item);
		next[item.id] = applyLockedMoneyFields(item.id, buildEditingEntry(item));
		changed = true;
	});
	if (changed) {
		editingValues.value = next;
	}
}

const parseDateValue = (value) => {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

const compareByIdDesc = (a = {}, b = {}) => {
	const idA = Number(a.id);
	const idB = Number(b.id);
	if (Number.isFinite(idA) && Number.isFinite(idB)) {
		return idB - idA;
	}
	return String(b.id || '').localeCompare(String(a.id || ''));
};

const sortPurchases = (items = []) =>
	[...items].sort((a = {}, b = {}) => {
		if (a?.__pending && !b?.__pending) return -1;
		if (!a?.__pending && b?.__pending) return 1;
		const dateA = parseDateValue(a.date);
		const dateB = parseDateValue(b.date);
		if (dateA && dateB) {
			const diff = dateB - dateA;
			if (diff !== 0) {
				return diff;
			}
			return compareByIdDesc(a, b);
		}
		if (dateA) return -1;
		if (dateB) return 1;
		return compareByIdDesc(a, b);
	});

const setItems = (nextItems = [], { sort = false } = {}) => {
	const prepared = sort ? sortPurchases(nextItems) : [...nextItems];
	store.$patch((state) => {
		state.items = prepared;
	});
};

const incrementPaginationTotal = () => {
	const current = store.pagination || {};
	const pageSize = Number(current.pageSize) || store.items.length || 1;
	const nextTotal = Number.isFinite(Number(current.total))
		? Number(current.total) + 1
		: store.items.length;
	const nextPageCount = Math.max(1, Math.ceil(nextTotal / pageSize));
	store.pagination = {
		...current,
		total: nextTotal,
		pageCount: nextPageCount,
	};
};

const refetchPage = async (page = store.pagination.page || 1) => {
	const allPages = store.hasActiveDateRange?.() || false;
	await store.fetchPurchases({ page, allPages });
	seedEditingValues();
};

const findPurchaseById = (id) => store.items.find((item) => item.id === id) || null;

const requestSave = (purchase, { debounce = true } = {}) => {
	const target = findPurchaseById(purchase?.id);
	if (target) {
		purchase = target;
	}
	if (!purchase?.id) return;
	if (debounce) {
		if (saveTimers[purchase.id]) {
			clearTimeout(saveTimers[purchase.id]);
		}
		saveTimers[purchase.id] = setTimeout(() => {
			saveTimers[purchase.id] = null;
			savePurchase(purchase);
		}, 350);
	} else {
		if (saveTimers[purchase.id]) {
			clearTimeout(saveTimers[purchase.id]);
			saveTimers[purchase.id] = null;
		}
		savePurchase(purchase);
	}
};

// A purchase created with "Add purchase" keeps its placeholder's key after saving,
// so its row isn't re-created while its entrance is still playing.
const rowKeyAliases = new Map();
const rowKey = (purchase) => rowKeyAliases.get(purchase.id) || purchase.id;
const newRowKeys = ref(new Set());
const markRowAsNew = (key) => {
	newRowKeys.value.add(key);
	setTimeout(() => newRowKeys.value.delete(key), 1500);
};

const createPendingPurchase = (date, { vendorId = '', buildingId = '' } = {}) => {
	const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
	const placeholder = {
		id: tempId,
		documentId: null,
		date,
		net: null,
		vat: null,
		gross: null,
		notes: '',
		documentStatus: '',
		vendor: vendorId ? { id: vendorId } : null,
		cos: null,
		building: buildingId ? { id: buildingId } : null,
		__pending: true,
	};
	setItems([placeholder, ...store.items], { sort: true });
	editingValues.value = {
		[tempId]: buildEditingEntry(placeholder),
		...editingValues.value,
	};
	setVatRateFor(tempId, DEFAULT_VAT_RATE);
	trimItemsForPlaceholder(tempId);
	return placeholder;
};

const replacePendingPurchase = (tempId, created) => {
	if (!created?.id) return false;
	const index = store.items.findIndex((item) => item.id === tempId);
	if (index === -1) {
		return false;
	}
	rowKeyAliases.set(created.id, tempId);
	const next = [...store.items];
	const previousDate = store.items[index]?.date || null;
	next[index] = { ...created };
	const dateChanged = (created.date || null) !== previousDate;
	setItems(next, { sort: dateChanged });
	const { [tempId]: _, ...rest } = editingValues.value;
	const entry = applyLockedMoneyFields(created.id, buildEditingEntry(created));
	editingValues.value = {
		...rest,
		[created.id]: entry,
	};
	const pendingRate = getStoredVatRate(tempId);
	if (pendingRate !== undefined) {
		setVatRateFor(created.id, pendingRate);
		deleteVatRateFor(tempId);
	} else {
		ensureVatRateEntry(created);
	}
	transferMoneyLocks(tempId, created.id);
	return true;
};

const getPageSizeLimit = () => {
	const size = Number(store.pagination.pageSize);
	if (Number.isFinite(size) && size > 0) {
		return size;
	}
	return store.items.length || 0;
};

const trimItemsForPlaceholder = (placeholderId) => {
	const limit = getPageSizeLimit();
	if (!limit || store.items.length <= limit) {
		return;
	}
	const nonPending = store.items.filter((item) => !item?.__pending);
	if (!nonPending.length) {
		return;
	}
	const sortedDescending = sortPurchases(nonPending);
	const overflowEntry = sortedDescending[sortedDescending.length - 1];
	if (!overflowEntry) {
		return;
	}
	const snapshot = removePurchaseLocally(overflowEntry);
	if (snapshot) {
		pendingOverflowSnapshots[placeholderId] = snapshot;
	}
};

const restoreTrimmedItemsForPlaceholder = (placeholderId) => {
	const snapshot = pendingOverflowSnapshots[placeholderId];
	if (!snapshot) return;
	restorePurchaseSnapshot(snapshot);
	delete pendingOverflowSnapshots[placeholderId];
};

const discardTrimmedItemsForPlaceholder = (placeholderId) => {
	if (pendingOverflowSnapshots[placeholderId]) {
		delete pendingOverflowSnapshots[placeholderId];
	}
};

const removePurchaseLocally = (purchase) => {
	if (!purchase?.id) {
		return null;
	}
	const index = store.items.findIndex((item) => item.id === purchase.id);
	if (index === -1) {
		return null;
	}
	const removedEntry = store.items[index];
	const next = [...store.items];
	next.splice(index, 1);
	setItems(next, { sort: true });
	const editingSnapshot = editingValues.value[purchase.id]
		? { ...editingValues.value[purchase.id] }
		: null;
	const nextEditing = { ...editingValues.value };
	delete nextEditing[purchase.id];
	editingValues.value = nextEditing;
	const vatRateSnapshot = getStoredVatRate(purchase.id);
	deleteVatRateFor(purchase.id);
	const moneyLocksSnapshot = (() => {
		const key = getMoneyLockKey(purchase.id);
		return key && moneyFieldLocks[key] ? { ...moneyFieldLocks[key] } : null;
	})();
	clearMoneyLocksFor(purchase.id);
	return {
		index,
		entry: removedEntry,
		editing: editingSnapshot,
		vatRate: vatRateSnapshot,
		moneyLocks: moneyLocksSnapshot,
	};
};

const restorePurchaseSnapshot = (snapshot) => {
	if (!snapshot?.entry || snapshot.index === undefined) {
		return false;
	}
	const next = [...store.items];
	const insertIndex = Math.min(snapshot.index, next.length);
	next.splice(insertIndex, 0, snapshot.entry);
	setItems(next, { sort: true });
	if (snapshot.entry?.id && snapshot.editing) {
		editingValues.value = {
			...editingValues.value,
			[snapshot.entry.id]: snapshot.editing,
		};
	}
	if (snapshot.entry?.id) {
		if (snapshot.vatRate !== undefined) {
			setVatRateFor(snapshot.entry.id, snapshot.vatRate);
		} else {
			ensureVatRateEntry(snapshot.entry);
		}
		if (snapshot.moneyLocks) {
			const key = getMoneyLockKey(snapshot.entry.id);
			if (key) {
				moneyFieldLocks[key] = { ...snapshot.moneyLocks };
			}
		}
	}
	return true;
};

const syncUpdatedPurchase = (updated, { snapshot } = {}) => {
	if (!updated?.documentId || !updated?.id) {
		return false;
	}
	const index = store.items.findIndex(
		(item) => item.documentId === updated.documentId || item.id === updated.id,
	);
	if (index === -1) {
		return false;
	}
	const next = [...store.items];
	const previousDate = store.items[index]?.date || null;
	const merged = applyLockedMoneyFields(updated.id, updated);
	next[index] = { ...merged };
	const dateChanged = (merged.date || null) !== previousDate;
	setItems(next, { sort: dateChanged });
	const entry = applyLockedMoneyFields(updated.id, buildEditingEntry(merged));
	const mergedEntry = mergeEditedFields(updated.id, entry, snapshot);
	editingValues.value = {
		...editingValues.value,
		[updated.id]: mergedEntry,
	};
	return true;
};

const parseDecimal = (value) => {
	if (value === null || value === undefined) return null;
	const text = String(value).trim();
	if (!text) return null;
	try {
		return Number(toBig(text).toFixed(2));
	} catch (error) {
		return null;
	}
};

const isRowSaving = (id) => !!savingState[id];

const savePayloadFor = (values) => ({
	date: values.date || null,
	vendor: values.vendorId || null,
	cos: values.cosId || null,
	building: values.buildingId || null,
	notes: values.notes || '',
	documentStatus: values.documentStatus || '',
	net: parseDecimal(values.net),
	vat: parseDecimal(values.vat),
	gross: parseDecimal(values.gross),
});

const savePurchase = async (purchase) => {
	if (!purchase?.documentId || !purchase?.id) {
		releaseVatRecalcGuard(purchase?.id);
		return;
	}
	if (savingState[purchase.id]) {
		queuedSaves[purchase.id] = true;
		return;
	}
	const currentPurchase = findPurchaseById(purchase.id) || purchase;
	const values = editingValues.value[purchase.id];
	if (!values) {
		releaseVatRecalcGuard(purchase.id);
		return;
	}
	const grossValue = parseMoneyValue(values.gross);
	const netValue = parseMoneyValue(values.net);
	const vatValue = parseMoneyValue(values.vat);
	if (grossValue !== null && netValue !== null && vatValue !== null) {
		const expectedGross = netValue.plus(vatValue);
		if (grossValue.minus(expectedGross).abs().gt(0.01)) {
			toast?.error?.('Gross must equal Net + VAT.');
			releaseVatRecalcGuard(purchase.id);
			return;
		}
	}
	savingState[purchase.id] = true;
	const originalDate = currentPurchase.date ? currentPurchase.date.substring(0, 10) : '';
	const nextDate = values.date || '';
	const dateChanged = originalDate !== nextDate;
	try {
		const snapshot = snapshotEditingValues(purchase.id);
		const updated = await store.updatePurchase(
			currentPurchase.documentId,
			savePayloadFor(values),
			currentPurchase,
		);
		const synced = syncUpdatedPurchase(updated, { snapshot });
		if (!synced || dateChanged) {
			await refetchPage(dateChanged ? 1 : store.pagination.page);
		}
	} catch (error) {
		logError(error, 'savePurchase');
		toast?.error?.(error?.message || 'Failed to save purchase');
	} finally {
		savingState[purchase.id] = false;
		if (queuedSaves[purchase.id]) {
			queuedSaves[purchase.id] = false;
			requestSave(findPurchaseById(purchase.id) || purchase, { debounce: false });
		} else {
			releaseVatRecalcGuard(purchase.id);
		}
	}
};

const deletePurchase = async (purchase) => {
	if (!purchase?.documentId || !purchase?.id) return;
	openSubmenuId.value = null;
	const snapshot = removePurchaseLocally(purchase);
	try {
		await store.deletePurchase(purchase.documentId, purchase);
		await refetchPage();
	} catch (error) {
		restorePurchaseSnapshot(snapshot);
		logError(error, 'deletePurchase');
		toast?.error?.(error?.message || 'Failed to delete purchase');
	}
};

const handleAdd = async () => {
	if (isAdding.value) return;
	isAdding.value = true;
	const today = new Date();
	const date = today.toLocaleDateString('en-CA');
	const vendorId = localFilters.vendorId || '';
	const buildingId = localFilters.buildingId || '';
	const isRangeMode = isDateRangeSelected.value;
	const placeholder = isRangeMode ? null : createPendingPurchase(date, { vendorId, buildingId });
	if (placeholder) markRowAsNew(placeholder.id);
	try {
		const created = await store.createPurchase({ date, vendorId, buildingId });
		if (isRangeMode) {
			await store.updateFilters({
				vendorId: localFilters.vendorId || null,
				buildingId: localFilters.buildingId || null,
				dateFrom: localFilters.dateFrom || null,
				dateTo: localFilters.dateTo || null,
			});
			// With a date range there is no placeholder; the saved purchase arrives with the refreshed list.
			if (created?.id) markRowAsNew(created.id);
			seedEditingValues();
		} else {
			const replaced = replacePendingPurchase(placeholder.id, created);
			if (replaced) {
				incrementPaginationTotal();
			} else {
				await refetchPage(1);
			}
		}
	} catch (error) {
		if (placeholder) {
			removePurchaseLocally(placeholder);
			restoreTrimmedItemsForPlaceholder(placeholder.id);
		}
		logError(error, 'createPurchase');
		toast?.error?.(error?.message || 'Failed to create purchase');
	} finally {
		if (placeholder) {
			discardTrimmedItemsForPlaceholder(placeholder.id);
		}
		isAdding.value = false;
	}
};

// Picking a value moves on to the next cell that still needs one, like in Appointments.
const CELL_ORDER = ['documentStatus', 'date', 'vendorId', 'cosId', 'buildingId', 'gross', 'net', 'vat', 'notes'];
const cellInputs = new Map();
const setCellInput = (id, field, input) => {
	const key = `${id}:${field}`;
	if (input) cellInputs.set(key, input);
	else cellInputs.delete(key);
};

const focusCell = (id, field) => nextTick(() => {
	const target = cellInputs.get(`${id}:${field}`);
	if (!target) return;
	if (field === 'date') {
		const trigger = target.$el?.querySelector?.('.drp-trigger');
		trigger?.focus();
		trigger?.click();
		return;
	}
	// Catalog cells open their list on focus.
	const element = target.$el ? target.$el.querySelector?.('input') : target;
	element?.focus?.();
});

const isEmptyCell = (value) => value === '' || value === null || value === undefined;

// Lists hand over up to Gross; Net and VAT are worked out from it.
const focusNextEmptyCell = (purchase, field, { until = 'gross' } = {}) => {
	const entry = editingValues.value[purchase.id];
	const start = CELL_ORDER.indexOf(field);
	if (!entry || start === -1) return false;
	const next = CELL_ORDER.slice(start + 1, CELL_ORDER.indexOf(until) + 1).find((name) => isEmptyCell(entry[name]));
	if (next) focusCell(purchase.id, next);
	return Boolean(next);
};

// Enter in an amount goes on to the next empty cell, or finishes the edit.
const handleMoneyEnter = (purchase, field, event) => {
	if (!focusNextEmptyCell(purchase, field, { until: 'notes' })) event.target.blur();
};

const handleStatusChange = (purchase) => {
	requestSave(purchase, { debounce: false });
	if (editingValues.value[purchase.id]?.documentStatus) focusNextEmptyCell(purchase, 'documentStatus');
};

const updateSelectValue = (purchase, field, value) => {
	const record = editingValues.value[purchase.id];
	if (!record) return;
	const normalized = value || '';
	if (record[field] === normalized) {
		return;
	}
	record[field] = normalized;
	requestSave(purchase, { debounce: false });
	// A new date re-sorts the list and moves the row away, so the focus stays put.
	if (normalized && field !== 'date') focusNextEmptyCell(purchase, field);
};

const changePage = async (page) => {
	try {
		await store.changePage(page);
		seedEditingValues();
	} catch (error) {
		logError(error, 'changePage');
	}
};

const refresh = async () => {
	try {
		await refetchPage();
	} catch (error) {
		logError(error, 'refreshPurchases');
	}
};

const toggleSubmenu = (id) => {
	openSubmenuId.value = openSubmenuId.value === id ? null : id;
};

const handleDateRangeUpdate = ({ dateFrom, dateTo }) => {
	localFilters.dateFrom = dateFrom || '';
	localFilters.dateTo = dateTo || '';
};

const removeFilter = (key) => {
	if (key === 'vendorId') localFilters.vendorId = '';
	if (key === 'buildingId') localFilters.buildingId = '';
	if (key === 'dateRange') {
		localFilters.dateFrom = '';
		localFilters.dateTo = '';
	}
	editingFilterType.value = null;
};

const handleClickOutside = (event) => {
	if (!event.target.closest('.action-wrapper')) {
		openSubmenuId.value = null;
	}
	if (editingFilterType.value && !event.target.closest('.filter-inline-editor')) {
		editingFilterType.value = null;
	}
};

let hasMounted = false;
onMounted(async () => {
	try {
		document.addEventListener('click', handleClickOutside, true);
		await store.initialize({ forceCatalogs: true });
		syncFiltersFromStore();
		seedEditingValues();
	} catch (error) {
		logError(error, 'PurchasesJournalView initialize');
	} finally {
		hasMounted = true;
	}
});

onActivated(async () => {
	if (!hasMounted || store.initialized) return;
	try {
		await store.initialize();
		seedEditingValues();
	} catch (error) {
		logError?.(error, 'PurchasesJournalView refresh after import');
	}
});

onBeforeUnmount(() => {
	if (filterTimer) {
		clearTimeout(filterTimer);
		filterTimer = null;
	}
	Object.keys(saveTimers).forEach((key) => {
		if (saveTimers[key]) {
			clearTimeout(saveTimers[key]);
			saveTimers[key] = null;
		}
	});
	document.removeEventListener('click', handleClickOutside, true);
});
onDeactivated(() => {
	openSubmenuId.value = null;
	editingFilterType.value = null;
});

// What was entered on another device shows up here without reloading. The page on screen is
// read again quietly, and only replaced while nothing is being edited, saved or added.
const isPurchasesBusy = () =>
	isLoading.value ||
	isAdding.value ||
	Boolean(openSubmenuId.value || editingFilterType.value) ||
	Object.keys(savingState).length > 0 ||
	Object.values(saveTimers).some(Boolean) ||
	Object.values(queuedSaves).some(Boolean) ||
	(store.items || []).some((item) => item?.__pending);

const refreshPurchasesQuietly = async ({ canApply }) => {
	if (!store.initialized || isPurchasesBusy()) return;
	const request = {
		page: store.pagination.page || 1,
		allPages: store.hasActiveDateRange?.() || false,
		filters: JSON.stringify(store.filters),
	};
	const result = await store.peekPurchases({ page: request.page, allPages: request.allPages });
	// The page or the filters changed while the request was out, or editing began.
	if (!result || (store.pagination.page || 1) !== request.page || JSON.stringify(store.filters) !== request.filters) return;
	if (!canApply() || isPurchasesBusy()) return;
	if (sameRecords(store.items, result.items)) return;
	const known = new Set((store.items || []).map((item) => rowKey(item)));
	store.$patch({ items: result.items, pagination: result.pagination });
	seedEditingValues();
	result.items.forEach((item) => {
		const key = rowKey(item);
		if (!known.has(key)) markRowAsNew(key);
	});
};

useLiveRefresh(refreshPurchasesQuietly, { isBusy: isPurchasesBusy });
</script>

<style scoped>
/* Remove number input arrows globally */
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

input[type='number'] {
	-moz-appearance: textfield;
}

.purchases-journal {
	display: flex;
	flex-direction: column;
	gap: 16px;
	box-sizing: border-box;
	min-width: 0;
	font: var(--ap-type-body);
	color: var(--text-color);
}

.action-btn {
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	gap: 8px;
	height: 34px;
	padding: 0 13px;
	border: 1px solid var(--border-color);
	background: var(--noc-background);
	border-radius: 8px;
	color: var(--text-color);
	font: var(--ap-type-control);
	white-space: nowrap;
	cursor: pointer;
	box-shadow: none;
}

.action-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.action-btn:focus-visible {
	outline: 2px solid rgba(0, 101, 255, 0.28);
	outline-offset: 1px;
}

.action-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

/* The same compact controls and motion used in Appointments. */
.filters-bar {
	display: flex;
	align-items: center;
	gap: 8px;
	flex: 1 1 320px;
	min-width: 0;
	max-width: 100%;
	min-height: 36px;
	padding: 2px 12px;
	box-sizing: border-box;
	flex-wrap: wrap;
	position: relative;
	background: var(--noc-background, #fff);
	z-index: 40;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	box-shadow: none;
}

.filters-bar.is-disabled {
	opacity: 0.7;
	pointer-events: none;
}

.filters-bar-start {
	display: flex;
	align-items: center;
	color: var(--text-color-secondary);
}

.filter-chip {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	height: 30px;
	padding: 0 4px 0 10px;
	border: 1px solid var(--border-color);
	border-radius: 6px;
	background: var(--noc-background);
	font: var(--ap-type-control);
	color: var(--text-color);
	white-space: nowrap;
	box-shadow: none;
}

.filter-chip-text {
	line-height: inherit;
}

.filter-chip-remove {
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	cursor: pointer;
	color: var(--text-color-secondary, #9ca3af);
	padding: 4px;
	border-radius: 4px;
	font-size: 0.8rem;
	transition: color 0.15s, background-color 0.15s;
}

.filter-chip-remove:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.filter-label-btn {
	display: inline-flex;
	align-items: center;
	height: 30px;
	padding: 0 10px;
	border: none;
	background: none;
	color: var(--text-color-secondary);
	font: var(--ap-type-control);
	cursor: pointer;
	border-radius: 6px;
	white-space: nowrap;
}

.filter-label-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.filter-inline-editor {
	position: relative;
	width: 100%;
	min-width: 0;
}

.filter-editor-search {
	width: 100%;
}

.filter-editor-search :deep(.field-input) {
	border: 1px solid var(--border-color);
	border-radius: 6px;
	padding: 6px 10px;
	font: var(--ap-type-body);
	height: 30px;
}

.filter-editor-search :deep(.field-input):focus {
	border-color: var(--noc-blue, #0065ff);
}

.filter-date-wrapper {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	max-width: 100%;
	border: 1px solid transparent;
	border-radius: 6px;
	box-sizing: border-box;
}

.filter-date-wrapper.has-value {
	border-color: var(--border-color);
}

.filter-date-wrapper :deep(.date-range-picker) {
	min-width: 0;
	max-width: 100%;
}

.filter-date-wrapper :deep(.drp-trigger) {
	border: none;
	background: transparent;
	padding: 0 10px;
	height: 28px;
	font: var(--ap-type-control);
	color: var(--text-color-secondary);
	border-radius: 6px;
	box-shadow: none;
	font-variant-numeric: tabular-nums;
}

.filter-date-wrapper :deep(.drp-trigger:hover:not(:disabled)) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.filter-date-wrapper :deep(.drp-trigger--active) {
	color: var(--text-color);
}

.filter-date-wrapper :deep(.drp-trigger-text) {
	color: inherit;
}

.purchase-list-container {
	background: transparent;
	width: 100%;
	max-width: 100%;
	min-width: 0;
}

.purchase-list-content {
	width: 100%;
	box-sizing: border-box;
	min-width: 1420px;
	border: none;
	border-inline: 1px solid var(--border-color);
	border-radius: 0;
	box-shadow: none;
	overflow: visible;
	position: relative;
}

.purchase-header {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 0 0 16px 0;
	background: transparent;
	flex-wrap: wrap;
}

.purchase-list {
	position: relative;
	width: 100%;
	min-width: 0;
	padding: 0;
}

.purchase-totals-footer {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 16px;
	width: 100%;
	margin-top: 12px;
}

.totals-footer-count {
	color: var(--text-color-secondary);
	font: var(--ap-type-caption);
	font-variant-numeric: tabular-nums;
	padding: 8px 16px;
}

.totals-footer-summary {
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: min(320px, 100%);
	margin-left: auto;
	font: var(--ap-type-caption);
	font-variant-numeric: tabular-nums;
}

.totals-line {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 16px;
	padding: 0 16px;
}

.totals-line.total {
	padding: 10px 16px;
	background: var(--noc-blue-05);
	border-radius: 6px;
	font: var(--ap-type-total);
	font-variant-numeric: tabular-nums;
}

.totals-label {
	color: var(--text-color-secondary, #777);
	white-space: nowrap;
}

.totals-value {
	color: var(--text-color, #000);
	white-space: nowrap;
	text-align: right;
}

.purchase-row {
	display: flex;
	align-items: stretch;
	background: var(--noc-background, #fff);
	border-bottom: 1px solid var(--border-color, #e5e5e5);
	min-width: 1420px;
	width: 100%;
	position: relative;
}

.purchase-header-row {
	background-color: #fafafa;
	border-top: 1px solid var(--border-color, #dcdcdc);
	border-bottom: 1px solid var(--border-color, #dcdcdc);
	border-radius: 0;
	overflow: hidden;
}

.purchase-header-row .field-header {
	display: flex;
	align-items: center;
	padding: 10px 16px;
	font: var(--ap-type-table-label);
	font-weight: 600;
	color: var(--text-color);
	text-transform: none;
}

.purchase-header-row .field-header.field-gross,
.purchase-header-row .field-header.field-net,
.purchase-header-row .field-header.field-vat {
	justify-content: center;
}

.header-actions {
	pointer-events: none;
}

.purchase-row.submenu-open {
	z-index: 30;
}

.purchase-row.is-pending {
	pointer-events: none;
}

.purchase-fields {
	flex: 1 1 auto;
	display: grid;
	grid-template-columns:
		minmax(120px, 1fr) minmax(100px, 1.5fr) minmax(135px, 1.5fr) minmax(135px, 1.5fr) minmax(180px, 2fr) minmax(130px, 1fr) minmax(130px, 1fr) minmax(130px, 1fr) minmax(200px, 2fr);
	grid-template-areas: 'status date vendor cos building gross net vat notes';
	min-width: 0;
	gap: 0;
}

.field-input-container {
	display: flex;
	align-items: stretch;
	min-width: 0;
	border-right: 1px solid var(--border-color, #e9e9e9);
}

.field-input-container:first-child {
	border-left: none;
}

.field-input-container:last-child {
	border-right: none;
}

.field-input-container.field-notes {
	border-right: none;
}

.field-status {
	grid-area: status;
}

.field-date {
	grid-area: date;
}

.field-date :deep(.date-range-picker) {
	width: 100%;
	min-width: 0;
}

.field-date :deep(.drp-trigger) {
	width: 100%;
	height: 100%;
	min-height: 40px;
	box-sizing: border-box;
	flex-direction: row-reverse;
	justify-content: space-between;
	padding: 10px 16px;
	border: 0;
	border-radius: 0;
	box-shadow: none;
	background: var(--noc-background, #fff);
	color: var(--text-color-secondary);
	font: var(--ap-type-body);
	font-variant-numeric: tabular-nums;
}

.field-date :deep(.drp-trigger:hover:not(:disabled)),
.field-date :deep(.drp-trigger[aria-expanded='true']) {
	background: var(--noc-background-secondary, #fafafa);
}

.field-date :deep(.drp-trigger:focus-visible) {
	outline: 1px solid var(--noc-blue);
	outline-offset: -1px;
}

.field-date :deep(.drp-trigger-text) {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.field-vendor {
	grid-area: vendor;
}

.field-cos {
	grid-area: cos;
}

.field-building {
	grid-area: building;
}

.field-gross {
	grid-area: gross;
}

.field-net {
	grid-area: net;
}

.field-vat {
	grid-area: vat;
}

.field-notes {
	grid-area: notes;
}

.field-input-container :deep(.catalog-search-input) {
	width: 100%;
}

.field-input-container :deep(.catalog-search-input .field-input),
.field-input {
	min-height: 40px;
	padding: 10px 16px;
	border: none;
	background-color: var(--noc-background, #fff);
	color: var(--text-color);
	font: var(--ap-type-body);
	outline: none;
	transition: background-color 0.1s ease;
	width: 100%;
	box-sizing: border-box;
}

.purchase-row .field-input-container :deep(.catalog-search-input .field-input:focus),
.purchase-row .field-input:focus {
	border: none !important;
	box-shadow: none;
}

.field-input.field-number {
	text-align: center;
}

.field-vat {
	position: relative;
}

.field-vat .field-input {
	padding-right: 32px;
}

.vat-toggle-btn {
	position: absolute;
	right: 4px;
	bottom: 3px;
	border: none;
	color: var(--text-color-secondary);
	background: var(--noc-background-secondary);
	font: var(--ap-type-label);
	padding: 1px 4px;
	cursor: pointer;
	border-radius: 4px;
}

.vat-toggle-btn:hover {
	background-color: color-mix(in srgb, var(--text-color-secondary) 8%, transparent);
}

.vat-toggle-btn:disabled {
	opacity: 0.6;
}

.field-input-container :deep(.catalog-search-input .field-input)::placeholder,
.field-input::placeholder {
	color: var(--text-color-secondary, #9ca3af);
	opacity: 0.4;
}

.field-input-container :deep(.catalog-search-input .field-input):hover,
.field-input:hover {
	background-color: var(--noc-background-secondary, #fafafa);
	cursor: text;
}

.field-input-container input[type='number'] {
	text-align: center;
}

.purchase-actions {
	display: flex;
	align-items: center;
	padding: 0 16px;
	background: var(--noc-background, #fff);
	position: sticky;
	right: calc(-1 * var(--purchase-page-gutter, 24px));
	z-index: 2;
	border-left: none;
	flex-shrink: 0;
	gap: 8px;
	align-self: stretch;
	justify-content: center;
}

.purchase-row {
	position: relative;
}

.purchase-header-row .purchase-actions {
	background: #fafafa;
}

@media (max-width: 1600px) {
	.purchase-fields {
		grid-template-columns:
			minmax(95px, 0.8fr) minmax(110px, 1.5fr) minmax(120px, 1.5fr) minmax(120px, 1.5fr) minmax(160px, 2fr) minmax(95px, 0.8fr) minmax(95px, 0.8fr) minmax(95px, 0.8fr) minmax(180px, 2fr);
	}
}

.action-wrapper {
	position: relative;
	display: flex;
	align-items: center;
}

.more-options-btn {
	background: none;
	border: none;
	cursor: pointer;
	color: var(--text-color-secondary);
	padding: 4px;
	border-radius: 4px;
	display: flex;
	align-items: center;
	line-height: 1;
}

.more-options-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

/* Row menus open with the same short fade-and-slide as the filter menus. */
.purchase-row :deep(.dropdown-menu),
.submenu {
	animation: row-menu-enter 160ms ease-out both;
}

.purchase-row :deep(.dropdown-menu.is-above) {
	--row-menu-enter-offset: 4px;
}

@keyframes row-menu-enter {
	from {
		opacity: 0;
		transform: translateY(var(--row-menu-enter-offset, -4px));
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (prefers-reduced-motion: reduce) {
	.purchase-row :deep(.dropdown-menu),
	.submenu {
		animation: none;
	}
}

.submenu {
	position: absolute;
	right: 0;
	top: 100%;
	background-color: var(--noc-background, #fff);
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 4px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	z-index: 40;
	min-width: 150px;
	padding: 4px 0;
}

.submenu-btn {
	display: block;
	width: 100%;
	padding: 8px 16px;
	background: none;
	border: none;
	color: var(--text-color);
	text-align: left;
	font: var(--ap-type-control);
	cursor: pointer;
	white-space: nowrap;
}

.submenu-btn.delete {
	color: var(--noc-red, #c45151);
}

.submenu-btn.delete:hover {
	background-color: var(--noc-red-10, rgba(196, 81, 81, 0.1));
	color: var(--noc-red, #c45151);
}

.submenu-btn:disabled {
	opacity: 0.5;
}

.outline-btn {
	border: 1px solid var(--border-color);
	background: var(--noc-background);
	color: var(--text-color);
	height: 34px;
	padding: 0 12px;
	border-radius: 8px;
	cursor: pointer;
	font: var(--ap-type-control);
}

.outline-btn:disabled {
	opacity: 0.5;
}

.inline-error {
	display: flex;
	justify-content: space-between;
	align-items: center;
	border: 1px solid var(--noc-red, #c45151);
	border-radius: 6px;
	padding: 12px 16px;
	color: var(--noc-red, #c45151);
}

.empty-state {
	position: relative;
	padding: 32px 16px;
	text-align: center;
	background: var(--noc-background, #fff);
	color: var(--text-color-secondary);
	font: var(--ap-type-body);
	border-radius: 0 0 8px 8px;
}

.pagination-bar {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	padding: 4px 16px;
	margin-top: 12px;
	border-radius: 8px;
	background: transparent;
	border: none;
	box-shadow: none;
	flex-wrap: wrap;
	gap: 16px;
}

.pagination-results {
	font: var(--ap-type-caption);
	font-variant-numeric: tabular-nums;
	color: var(--text-color-secondary);
	white-space: nowrap;
}

.pagination-controls {
	display: flex;
	align-items: center;
	gap: 8px;
}

.pagination-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 34px;
	height: 34px;
	padding: 0 8px;
	border: 1px solid var(--border-color);
	background: var(--noc-background);
	border-radius: 8px;
	color: var(--text-color);
	font: var(--ap-type-control);
	font-variant-numeric: tabular-nums;
	cursor: pointer;
	box-shadow: none;
}

.pagination-btn:hover:not(:disabled):not(.active) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.pagination-btn.active {
	background-color: var(--noc-blue-05);
	border-color: var(--noc-blue-15);
	color: var(--noc-blue);
	cursor: default;
}

.pagination-btn:disabled {
	opacity: 0.4;
	cursor: not-allowed;
}

.pagination-ellipsis {
	color: var(--text-color-secondary, #999);
	padding: 0 4px;
}

.skeleton-block {
	height: 220px;
	background: var(--noc-background, #ffffff);
	border: 1px solid transparent;
	border-radius: 8px;
}

.table-loading-overlay {
	position: absolute;
	inset: 0;
	background: rgba(255, 255, 255, 0.82);
	z-index: 20;
	pointer-events: auto;
}

.table-loading-indicator {
	position: absolute;
	top: 10px;
	left: 50%;
	transform: translateX(-50%);
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 6px 10px;
	border-radius: 8px;
	border: 1px solid var(--border-color);
	background: var(--noc-background);
	color: var(--text-color-secondary);
	font: var(--ap-type-caption);
}

.table-loading-spinner {
	width: 14px;
	height: 14px;
	border-radius: 50%;
	border: 2px solid #d3d7df;
	border-top-color: var(--noc-blue, #0065ff);
	animation: table-loading-spin 0.6s linear infinite;
}

@keyframes table-loading-spin {
	to {
		transform: rotate(360deg);
	}
}

@keyframes shimmer {
	0% {
		background-position: 100% 0;
	}

	100% {
		background-position: 0 0;
	}
}
button {
	transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

button:focus-visible {
	outline: 2px solid var(--noc-blue);
	outline-offset: 2px;
}

.purchase-list > .purchase-row:not(:last-child) {
	border-image: linear-gradient(to right, transparent 16px, var(--border-color) 16px) 1;
}

.field-number {
	font-variant-numeric: tabular-nums;
}

.totals-line.total .totals-label {
	color: var(--text-color);
}

.outline-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

/* The shared calendar and catalog menus inherit the journal type scale. */
.purchases-journal :deep(.drp-dropdown) { border-radius: 8px; }
.purchases-journal :deep(.drp-btn) { border-radius: 8px; min-height: 34px; }
.purchases-journal :deep(.drp-month-title) { font: var(--ap-type-section); color: var(--text-color); }
.purchases-journal :deep(.drp-month-cell),
.purchases-journal :deep(.drp-btn) { font: var(--ap-type-control); }
.purchases-journal :deep(.drp-cell-num),
.purchases-journal :deep(.drp-preset-btn),
.purchases-journal :deep(.dropdown-menu) { font: var(--ap-type-body); }
.purchases-journal :deep(.drp-weekday) { font: var(--ap-type-label); }
.purchases-journal :deep(.drp-footer-range) { font: var(--ap-type-caption); }
.purchases-journal :deep(.drp-cell-num),
.purchases-journal :deep(.drp-footer-range) { font-variant-numeric: tabular-nums; }
.purchases-journal :deep(.drp-nav-btn:hover:not(:disabled)),
.purchases-journal :deep(.drp-month-title--clickable:hover:not(:disabled)),
.purchases-journal :deep(.drp-preset-btn:not(.drp-preset-btn--active):hover:not(:disabled)),
.purchases-journal :deep(.drp-month-cell:not(.drp-month-cell--selected):hover:not(:disabled)),
.purchases-journal :deep(.drp-btn--cancel:hover:not(:disabled)),
.purchases-journal :deep(.drp-cell:not(.drp-cell--empty):not(.drp-cell--selected):hover:not(:disabled) .drp-cell-num) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.purchases-journal :deep(.drp-btn--update:hover:not(:disabled)),
.purchases-journal :deep(.drp-month-cell--selected:hover:not(:disabled)),
.purchases-journal :deep(.drp-cell--selected:hover:not(:disabled) .drp-cell-num) {
	background-color: var(--primary-hover);
	color: var(--noc-white);
}

.purchases-journal :deep(.drp-cell--selected .drp-cell-num),
.purchases-journal :deep(.drp-month-cell--selected),
.purchases-journal :deep(.drp-preset-btn--active) {
	font-weight: 500;
}

.purchases-journal :deep(.drp-cell--today:not(.drp-cell--selected) .drp-cell-num) {
	font-weight: 700;
}
</style>
